/**
 * Batch Progress Viewer
 *
 * Real-time progress display for running batch simulations.
 * Shows overall progress, individual run statuses, and live updates.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse
} from '@mui/material';
import {
  CheckCircle as CompleteIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  PlayArrow as RunningIcon,
  Stop as CancelIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon
} from '@mui/icons-material';
import {
  getAdvancedSimulationBatchStatus,
  cancelAdvancedSimulationBatch
} from '../../../services/advancedSimulationApi';

function BatchProgressViewer({ batchId, token, onComplete }) {
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [expandedRuns, setExpandedRuns] = useState(new Set());

  const fetchStatus = useCallback(async () => {
    try {
      const status = await getAdvancedSimulationBatchStatus(batchId, token);
      setBatch(status);

      if (['completed', 'partial_failure', 'failed', 'cancelled'].includes(status.status)) {
        onComplete?.(status);
      }
    } catch (err) {
      setError('Failed to fetch batch status');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [batchId, token, onComplete]);

  // Poll for status while running
  useEffect(() => {
    fetchStatus();

    const interval = setInterval(() => {
      if (batch?.status === 'running') {
        fetchStatus();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [fetchStatus, batch?.status]);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this batch?')) return;

    setCancelling(true);
    try {
      await cancelAdvancedSimulationBatch(batchId, token);
      fetchStatus();
    } catch (err) {
      setError('Failed to cancel batch');
    } finally {
      setCancelling(false);
    }
  };

  const toggleRunExpanded = (runId) => {
    setExpandedRuns(prev => {
      const next = new Set(prev);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CompleteIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'running':
        return <RunningIcon color="primary" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'primary';
      case 'cancelled':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Loading batch progress...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!batch) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Batch not found
      </Alert>
    );
  }

  const progress = batch.progress || { completed: 0, failed: 0, pending: 0, running: 0 };
  const totalRuns = progress.completed + progress.failed + progress.pending + progress.running;
  const completedRuns = progress.completed + progress.failed;
  const progressPercent = totalRuns > 0 ? (completedRuns / totalRuns) * 100 : 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Batch Progress: {batch.name}
      </Typography>

      {/* Overall Progress */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Overall Progress
              </Typography>
              <Typography variant="h4">
                {completedRuns} / {totalRuns}
              </Typography>
            </Box>
            <Chip
              label={batch.status}
              color={getStatusColor(batch.status)}
              size="large"
            />
          </Box>

          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{ height: 10, borderRadius: 5, mb: 2 }}
          />

          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="success.main">{progress.completed}</Typography>
                <Typography variant="caption">Completed</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="primary.main">{progress.running}</Typography>
                <Typography variant="caption">Running</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="text.secondary">{progress.pending}</Typography>
                <Typography variant="caption">Pending</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="error.main">{progress.failed}</Typography>
                <Typography variant="caption">Failed</Typography>
              </Box>
            </Grid>
          </Grid>

          {batch.status === 'running' && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Cancel Batch'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Run Details */}
      {batch.runs && batch.runs.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Individual Runs
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40}></TableCell>
                    <TableCell>Run ID</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Therapist</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Turns</TableCell>
                    <TableCell>Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batch.runs.map((run) => (
                    <React.Fragment key={run._id}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => toggleRunExpanded(run._id)}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {expandedRuns.has(run._id) ? <CollapseIcon /> : <ExpandIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {run.runId?.substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>{run.configSnapshot?.clientPersonaKey}</TableCell>
                        <TableCell>{run.configSnapshot?.therapistPersonaKey}</TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(run.status)}
                            label={run.status}
                            size="small"
                            color={getStatusColor(run.status)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{run.metrics?.totalTurns || run.turns?.length || 0}</TableCell>
                        <TableCell>
                          ${(run.metrics?.estimatedCost || 0).toFixed(4)}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0 }}>
                          <Collapse in={expandedRuns.has(run._id)} timeout="auto">
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Grid container spacing={2}>
                                <Grid item xs={6} md={3}>
                                  <Typography variant="caption" color="text.secondary">
                                    Total Tokens
                                  </Typography>
                                  <Typography variant="body2">
                                    {run.metrics?.totalTokens || 0}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                  <Typography variant="caption" color="text.secondary">
                                    Avg Latency
                                  </Typography>
                                  <Typography variant="body2">
                                    {run.metrics?.avgLatencyMs?.toFixed(0) || 0}ms
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                  <Typography variant="caption" color="text.secondary">
                                    Quality Score
                                  </Typography>
                                  <Typography variant="body2">
                                    {run.metrics?.qualityScore?.toFixed(2) || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                  <Typography variant="caption" color="text.secondary">
                                    Variation
                                  </Typography>
                                  <Typography variant="body2">
                                    {run.configSnapshot?.promptVariation || 'default'}
                                  </Typography>
                                </Grid>
                              </Grid>

                              {run.error && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                  {run.error}
                                </Alert>
                              )}

                              {run.turns && run.turns.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Last Message:
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                    "{run.turns[run.turns.length - 1]?.content?.substring(0, 150)}..."
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Timing Info */}
      <Box sx={{ mt: 2, display: 'flex', gap: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Started: {new Date(batch.createdAt).toLocaleString()}
        </Typography>
        {batch.completedAt && (
          <Typography variant="body2" color="text.secondary">
            Completed: {new Date(batch.completedAt).toLocaleString()}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default BatchProgressViewer;
